import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

// Components
import { defineMessages, FormattedMessage, useIntl } from 'dibs-react-intl';
import ShareIconLegacy from 'dibs-icons/exports/legacy/Share.ts';
import ShareIcon from 'dibs-icons/exports/Share.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import { PdpSharedSocialSharePinterest } from '../shared/PdpSharedSocialShare/PdpSharedSocialSharePinterest.tsx';
import { PdpSharedSocialShareFacebook } from '../shared/PdpSharedSocialShare/PdpSharedSocialShareFacebook.tsx';
import { PdpSharedSocialShareEmail } from '../shared/PdpSharedSocialShare/PdpSharedSocialShareEmail.tsx';

import { type PdpRespIconBubbleShare_viewer$key } from './__generated__/PdpRespIconBubbleShare_viewer.graphql.ts';
import { type PdpRespIconBubbleShare_user$key } from './__generated__/PdpRespIconBubbleShare_user.graphql.ts';
import { type PdpRespIconBubbleShare_item$key } from './__generated__/PdpRespIconBubbleShare_item.graphql.ts';

import { PdpRespIconBubbleWithTooltip } from './PdpRespIconBubbleWithTooltip.tsx';

// messages
const messages = defineMessages({
    share: { id: 'PdpRespIconBubbleShare.icon', defaultMessage: 'Share' },
});

const pinterestLabel = (
    <FormattedMessage
        id="PdpRespIconBubbleShare.pinterestCta"
        defaultMessage="Share on Pinterest"
    />
);

const facebookLabel = (
    <FormattedMessage id="PdpRespIconBubbleShare.facebookCta" defaultMessage="Share on Facebook" />
);

const emailLabel = (
    <FormattedMessage id="PdpRespIconBubbleShare.emailCta" defaultMessage="Share via Email" />
);

type Props = {
    item: PdpRespIconBubbleShare_item$key;
    viewer: PdpRespIconBubbleShare_viewer$key;
    user?: PdpRespIconBubbleShare_user$key | null | undefined;
    skuId?: string | null;
};

const itemFragment = graphql`
    fragment PdpRespIconBubbleShare_item on Item {
        ...PdpSharedSocialSharePinterest_item
        ...PdpSharedSocialShareFacebook_item
        ...PdpSharedSocialShareEmail_item
    }
`;

const viewerFragment = graphql`
    fragment PdpRespIconBubbleShare_viewer on Viewer {
        ...PdpSharedSocialShareEmail_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespIconBubbleShare_user on User {
        ...PdpSharedSocialShareEmail_user
    }
`;

export const PdpRespIconBubbleShare: FC<Props> = ({
    skuId,
    viewer: viewerRef,
    item: itemRef,
    user: userRef = null,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { isPDPLargerItemsVariant } = useServerVarsContext();
    const intl = useIntl();

    return (
        <PdpRespIconBubbleWithTooltip
            icon={isPDPLargerItemsVariant ? () => <ShareIcon variation="24" /> : ShareIconLegacy}
            label={intl.formatMessage(messages.share)}
        >
            <PdpSharedSocialSharePinterest
                variant="iconWithLabel"
                item={item}
                label={pinterestLabel}
            />
            <PdpSharedSocialShareFacebook
                variant="iconWithLabel"
                item={item}
                label={facebookLabel}
            />
            <PdpSharedSocialShareEmail
                variant="iconWithLabel"
                item={item}
                user={user}
                viewer={viewer}
                skuId={skuId}
                label={emailLabel}
            />
        </PdpRespIconBubbleWithTooltip>
    );
};
